import { useEffect, useState } from "react";
import { toast } from "sonner";
import API, { fmt, apiError } from "@/lib/api";
import { PageHeader, StatCard, StatusPill, Loader } from "@/components/ui-bits";
import { Gem, Layers, TrendingUp, Wallet, Lock, Unlock, Loader2, Copy, Upload, Zap, X } from "lucide-react";

function Countdown({ until }) {
  const [left, setLeft] = useState("");
  useEffect(() => {
    const tick = () => {
      const ms = new Date(until) - new Date();
      if (ms <= 0) return setLeft("expired");
      const h = Math.floor(ms / 3.6e6), m = Math.floor((ms % 3.6e6) / 6e4), s = Math.floor((ms % 6e4) / 1000);
      setLeft(`${h}h ${m}m ${s}s`);
    };
    tick(); const id = setInterval(tick, 1000); return () => clearInterval(id);
  }, [until]);
  return <span className="font-num">{left}</span>;
}

export default function SilverCard() {
  const [d, setD] = useState(null);
  const [st, setSt] = useState(null);
  const [drops, setDrops] = useState([]);
  const [dropping, setDropping] = useState(false);
  const [showFund, setShowFund] = useState(false);
  const [tx, setTx] = useState("");
  const [proofPath, setProofPath] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [qty, setQty] = useState(6);

  const load = () => {
    API.get("/silver-cards").then((r) => setD(r.data));
    API.get("/silver-card/status").then((r) => setSt(r.data));
    API.get("/silver-card/drops").then((r) => setDrops(r.data.drops));
  };
  useEffect(() => { load(); }, []);

  const drop = async () => {
    setDropping(true);
    try { const r = await API.post("/silver-card/drop"); toast.success(r.data.message); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setDropping(false); }
  };

  const uploadProof = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await API.post("/upload/proof", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProofPath(r.data.proof_path); toast.success("Proof uploaded");
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };

  const submitFund = async () => {
    if (!proofPath) return toast.error("Please upload your payment proof");
    const unit = st.silver_card_unit_price || 1;
    const minQty = st.silver_card_min_qty || 6;
    const n = parseInt(qty, 10) || 0;
    if (n < minQty) return toast.error(`Buy at least ${minQty} Silver Cards`);
    setSubmitting(true);
    try {
      const r = await API.post("/silver-card/fund-request", { amount: n * unit, tx_hash: tx, proof_path: proofPath });
      toast.success(r.data.message); setShowFund(false); setTx(""); setProofPath(""); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSubmitting(false); }
  };

  const openFund = () => { setQty(st?.silver_card_min_qty || 6); setShowFund(true); };

  if (!d || !st) return <Loader label="Loading Silver Cards" />;

  return (
    <div>
      <PageHeader title="Silver Card"
        subtitle={`Buy Silver Cards ($${st.silver_card_unit_price || 1} each, min ${st.silver_card_min_qty || 6}) and drop a card ($${st.drop_cost}) to unlock your Main wallet for ${st.main_active_hours}h.`}
        testid="silver-header" />

      {/* Silver Card wallet + drop */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="glass rounded-2xl p-6" data-testid="silver-wallet-card">
          <div className="flex items-center justify-between">
            <span className="text-[0.7rem] uppercase font-mono tracking-[0.18em] text-slate-400">Silver Card Wallet</span>
            <Wallet className="h-5 w-5 text-cyan-400" />
          </div>
          <div className="font-num text-3xl font-bold text-white mt-2" data-testid="silver-wallet-balance">{fmt(st.silver_card_wallet)}</div>
          <button data-testid="get-silver-card-btn" onClick={openFund}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold py-2.5 rounded-xl">
            <Gem className="h-4 w-4" /> Buy Silver Cards (${st.silver_card_unit_price || 1} each)
          </button>
        </div>

        <div className="glass rounded-2xl p-6" data-testid="silver-drop-card">
          <div className="flex items-center justify-between">
            <span className="text-[0.7rem] uppercase font-mono tracking-[0.18em] text-slate-400">Drop a Silver Card</span>
            <Zap className="h-5 w-5 text-amber-400" />
          </div>
          <p className="text-xs text-slate-500 mt-2">Costs ${st.drop_cost} from Silver Card Wallet. Unlocks Main wallet for {st.main_active_hours}h. One drop every {st.cooldown_hours}h.</p>
          <button data-testid="drop-silver-card-btn" onClick={drop} disabled={dropping || !st.can_drop}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-slate-900 font-semibold py-2.5 rounded-xl">
            {dropping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />} Drop Silver Card
          </button>
          {!st.can_drop && st.next_drop_at && new Date(st.next_drop_at) > new Date() && (
            <p className="text-[0.7rem] text-amber-400 mt-2 text-center">Next drop in <Countdown until={st.next_drop_at} /></p>
          )}
          {!st.can_drop && st.silver_card_wallet < st.drop_cost && (
            <p className="text-[0.7rem] text-slate-500 mt-2 text-center">Add funds to drop a card.</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <StatCard testid="silver-total" label="Silver Cards" value={d.total_cards} icon={Gem} accent="cyan" />
        <StatCard testid="silver-completed" label="Completed" value={d.completed} icon={Layers} accent="emerald" />
        <StatCard testid="silver-earned" label="Total Earned" value={d.total_earned} money icon={TrendingUp} accent="gold" />
      </div>

      <div className="glass rounded-2xl p-6 mb-6" data-testid="silver-cards-list">
        <h3 className="font-display text-lg font-semibold text-white flex items-center gap-2 mb-4">
          <Gem className="h-5 w-5 text-cyan-400" /> Your Silver Cards
        </h3>
        {d.cards.length === 0 ? (
          <p className="text-sm text-slate-500 py-8 text-center">No Silver Cards yet. Buy Silver Cards (${st.silver_card_unit_price || 1} each, min {st.silver_card_min_qty || 6}) to get started.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-white/10">
                <th className="py-2 pr-4">Granted</th><th className="py-2 pr-4">Status</th><th className="py-2 pr-4">Payout</th><th className="py-2 text-right">Completed</th>
              </tr></thead>
              <tbody>
                {d.cards.map((c) => (
                  <tr key={c.id} className="border-b border-white/5 last:border-0">
                    <td className="py-3 pr-4 font-num text-slate-400">{new Date(c.granted_at).toLocaleDateString()}</td>
                    <td className="py-3 pr-4"><StatusPill status={c.status === "completed" ? "completed" : "pending"} /></td>
                    <td className="py-3 pr-4 font-num text-emerald-300">{c.status === "completed" ? fmt(c.holder_payout) : "—"}</td>
                    <td className="py-3 text-right font-num text-slate-400">{c.completed_at ? new Date(c.completed_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="glass rounded-2xl p-6 mb-6" data-testid="silver-drop-history">
        <h3 className="font-display text-lg font-semibold text-white flex items-center gap-2 mb-4">
          <Zap className="h-5 w-5 text-amber-400" /> Silver Card Drop History
        </h3>
        {drops.length === 0 ? (
          <p className="text-sm text-slate-500 py-8 text-center">No Silver Card drops yet. Drop a card to unlock your Main wallet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-white/10">
                <th className="py-2 pr-4">Dropped</th><th className="py-2 pr-4">Cost</th><th className="py-2 text-right">Main unlocked until</th>
              </tr></thead>
              <tbody>
                {drops.map((dr) => (
                  <tr key={dr.id} className="border-b border-white/5 last:border-0" data-testid="drop-history-row">
                    <td className="py-3 pr-4 font-num text-slate-300">{new Date(dr.dropped_at).toLocaleString()}</td>
                    <td className="py-3 pr-4 font-num text-amber-300">{fmt(dr.cost)}</td>
                    <td className="py-3 text-right font-num text-slate-400">{dr.main_active_until ? new Date(dr.main_active_until).toLocaleString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Buy Silver Cards dialog */}
      {showFund && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="silver-fund-dialog">
          <div className="glass rounded-2xl p-6 w-full max-w-md border border-white/10">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg font-semibold text-white">Buy Silver Cards</h3>
              <button onClick={() => setShowFund(false)}><X className="h-5 w-5 text-slate-400 hover:text-white" /></button>
            </div>
            <p className="text-xs text-slate-400 mb-3">${st.silver_card_unit_price || 1} per Silver Card, minimum {st.silver_card_min_qty || 6} cards. Send the total ({st.pay_network}) to the address below, then upload your payment proof. Admin approval credits your Silver Card Wallet.</p>

            <label className="text-xs text-slate-400">Number of cards</label>
            <input data-testid="silver-qty-input" type="number" min={st.silver_card_min_qty || 6} value={qty}
              onChange={(e) => setQty(e.target.value)}
              className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-white outline-none mt-1 mb-2 text-sm font-num" />
            <div className="flex items-center justify-between rounded-xl bg-cyan-500/10 border border-cyan-500/30 px-4 py-2.5 mb-3">
              <span className="text-xs text-slate-300">Total to pay</span>
              <span className="font-num text-lg font-bold text-cyan-300" data-testid="silver-total">{fmt((parseInt(qty, 10) || 0) * (st.silver_card_unit_price || 1))}</span>
            </div>

            <div className="flex items-center justify-between gap-2 rounded-xl bg-white/5 border border-white/10 px-3 py-2 mb-3">
              <span className="font-num text-xs text-emerald-300 break-all">{st.pay_address}</span>
              <button data-testid="silver-copy-addr" onClick={() => { navigator.clipboard.writeText(st.pay_address); toast.success("Address copied"); }}
                className="shrink-0 inline-flex items-center gap-1 text-[0.65rem] px-2 py-1 rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/30"><Copy className="h-3 w-3" /> Copy</button>
            </div>
            <input data-testid="silver-tx-input" value={tx} onChange={(e) => setTx(e.target.value)} placeholder="Transaction hash (optional)"
              className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-white outline-none mb-3 text-sm font-num" />
            <label className="flex items-center justify-center gap-2 w-full rounded-xl border border-dashed border-white/20 py-3 cursor-pointer text-sm text-slate-300 hover:bg-white/5 mb-4">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {proofPath ? "Proof uploaded ✓" : "Upload payment proof"}
              <input data-testid="silver-proof-input" type="file" accept="image/*" className="hidden" onChange={(e) => uploadProof(e.target.files[0])} />
            </label>
            <button data-testid="silver-fund-submit" onClick={submitFund} disabled={submitting || !proofPath}
              className="w-full inline-flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-60 text-slate-900 font-semibold py-3 rounded-xl">
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />} Submit Purchase Request
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
